import React, { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";
import { useApp } from "../../context/AppContext";

const DEPARTURE_REASONS = [
    { id: "resignation", label: "Démission" },
    { id: "end_of_contract", label: "Fin de contrat" },
    { id: "dismissal", label: "Licenciement" },
    { id: "retirement", label: "Retraite" },
    { id: "other", label: "Autre" },
];

export function OffboardingForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("high");
    const { users } = useApp();
    const [employeeId, setEmployeeId] = useState("");
    const [lastWorkingDay, setLastWorkingDay] = useState("");
    const [reason, setReason] = useState("resignation");
    const [returnLocation, setReturnLocation] = useState("");
    const [handoverUserId, setHandoverUserId] = useState("");

    const selectedEmployee = users.find((u) => u.id === employeeId);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            title: baseForm.title || `Départ - ${selectedEmployee?.first_name} ${selectedEmployee?.last_name}`,
            description: baseForm.description,
            priority: baseForm.priority,
            dynamic_fields: {
                employee_id: employeeId,
                last_working_day: lastWorkingDay,
                reason: reason,
                equipment_return_location: returnLocation,
                data_handover_to_user_id: handoverUserId || null,
                checklist_items: [
                    { item: "Désactivation compte AD", category: "IT", status: "pending", note: "" },
                    { item: "Récupération badge", category: "Sécurité", status: "pending", note: "" },
                    { item: "Récupération matériel", category: "IT", status: "pending", note: "" },
                    { item: "Transfert emails", category: "IT", status: "pending", note: "" },
                    { item: "Clôture accès VPN", category: "IT", status: "pending", note: "" },
                ],
            },
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            <BaseFormFields
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                {...baseForm}
                onTitleChange={onTitleChange}
                    allowedPriorities={allowedPriorities}
                titlePlaceholder="Ex: Départ - Marc Lefebvre (Commercial)"
                descriptionPlaceholder="Informations complémentaires sur le départ..."
            >
                <div className="form-group">
                    <Label className="form-label">Collaborateur concerné</Label>
                    <Select value={employeeId} onValueChange={setEmployeeId}>
                        <SelectTrigger data-testid="employee-select">
                            <SelectValue placeholder="Sélectionner un collaborateur" />
                        </SelectTrigger>
                        <SelectContent>
                            {users.filter((u) => u.is_active).map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                    {u.first_name} {u.last_name} ({u.email})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <Label className="form-label">Dernier jour de travail</Label>
                        <Input
                            type="date"
                            value={lastWorkingDay}
                            onChange={(e) => setLastWorkingDay(e.target.value)}
                            data-testid="last-working-day"
                        />
                    </div>

                    <div className="form-group">
                        <Label className="form-label">Motif du départ</Label>
                        <Select value={reason} onValueChange={setReason}>
                            <SelectTrigger data-testid="departure-reason">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {DEPARTURE_REASONS.map((r) => (
                                    <SelectItem key={r.id} value={r.id}>
                                        {r.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="form-group">
                    <Label className="form-label">Lieu de retour du matériel</Label>
                    <Input
                        value={returnLocation}
                        onChange={(e) => setReturnLocation(e.target.value)}
                        placeholder="Ex: Bureau IT - RDC"
                        data-testid="return-location"
                    />
                </div>

                <div className="form-group">
                    <Label className="form-label">Transfert des données vers</Label>
                    <Select value={handoverUserId} onValueChange={setHandoverUserId}>
                        <SelectTrigger data-testid="handover-user">
                            <SelectValue placeholder="Sélectionner (optionnel)" />
                        </SelectTrigger>
                        <SelectContent>
                            {users.filter((u) => u.id !== employeeId && u.is_active).map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                    {u.first_name} {u.last_name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="p-4 bg-gray-50 border rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                        Checklist de départ (générée automatiquement)
                    </h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                        <li>• Désactivation compte Active Directory</li>
                        <li>• Récupération du badge d'accès</li>
                        <li>• Récupération du matériel informatique</li>
                        <li>• Configuration du transfert d'emails</li>
                        <li>• Clôture des accès VPN</li>
                    </ul>
                </div>
            </BaseFormFields>

            <div className="mt-6 flex justify-end">
                <Button type="submit" disabled={!baseForm.title || submitting} data-testid="submit-ticket">
                    {submitting ? "Création..." : "Soumettre le ticket"}
                </Button>
            </div>
        </form>
    );
}
